import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { cn } from '@/lib/utils';

import { INTERACTIVE_STATES } from './tokens';

export interface LinkProps extends Omit<
  React.ComponentProps<typeof RouterLink>,
  'onClick' | 'className'
> {
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  href?: string; // For external links
  target?: '_blank' | '_self' | '_parent' | '_top';
  rel?: string;
  title?: string;
  variant?: 'default' | 'subtle' | 'muted';
  className?: string;
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(({
  children,
  onClick,
  href,
  target,
  rel,
  title,
  to,
  variant = 'default',
  className,
  ...routerProps
}, ref) => {
  const {
    className: routerClassName,
    style,
    ...otherRouterProps
  } = routerProps as {
    className?: string;
    style?: React.CSSProperties;
    [key: string]: unknown;
  };
  const linkClasses = cn(
    `${INTERACTIVE_STATES.transitions.all} ease-in-out inline-flex items-center`,
    {
      'text-primary hover:text-primary/80': variant === 'default',
      'text-foreground hover:text-primary': variant === 'subtle',
      'text-muted-foreground hover:text-foreground': variant === 'muted',
    },
    className
  );
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Call the original onClick if provided
    onClick?.(event);

    // If href is provided, this is an external link - let browser handle it
    if (href) return;

    // Check if Ctrl/Cmd key is pressed
    if (event.metaKey || event.ctrlKey) {
      // Prevent React Router navigation
      event.preventDefault();

      // Create a regular anchor element for navigation in new tab
      const link = document.createElement('a');
      link.href = typeof to === 'string' ? to : (to.pathname ?? '');
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      // Add search params and hash if present
      if (typeof to === 'object') {
        if (to.search) link.href += to.search;
        if (to.hash) link.href += to.hash;
      }

      // Navigate to the link
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // If href is provided, render as regular anchor tag
  if (href) {
    return (
      <a
        ref={ref}
        href={href}
        target={target}
        rel={rel}
        title={title}
        onClick={handleClick}
        className={cn(linkClasses, routerClassName as string)}
        style={style as React.CSSProperties}
        {...otherRouterProps}
      >
        {children}
      </a>
    );
  }

  // Otherwise, render as React Router Link
  return (
    <RouterLink
      ref={ref}
      to={to}
      title={title}
      onClick={handleClick}
      className={cn(linkClasses, routerClassName as string)}
      {...otherRouterProps}
    >
      {children}
    </RouterLink>
  );
});

Link.displayName = 'Link';
